import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { costCentersByCompanyID } from "graphql/queries";
import { updateCostCenter } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { CostCenterCreateForm, CostCenterUpdateForm } from ".";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const CostCenters = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "cost-center:create",
    "cost-center:update",
    "cost-center:delete",
  ]);
  const [costCenters, setCostCenters] = useState([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [costCenterDetails, setCostCenterDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    costCenterId: null,
    costCenterName: null,
  });
  const [modalCostCenter, setModalCostCenter] = useState({
    visible: false,
    costCenterId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      costCenters.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                costCenterDetails?.id === item.id
                  ? strings.costCenters.hide
                  : strings.costCenters.show
              }
              variation="link"
              size="small"
              onClick={() => {
                costCenterDetails?.id === item.id
                  ? setCostCenterDetails(null)
                  : setCostCenterDetails(item);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.costCenters.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.costCenters.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    costCenterId: item.id,
                    costCenterName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [costCenters, costCenterDetails?.id]
  );

  const onNext = () => {
    fetchCostCenters(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchCostCenters(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchCostCenters(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalCostCenter({ visible: true, action, costCenterId: id });
  const onSuccessCostCenter = () => {
    setModalCostCenter({ visible: false });
    fetchCostCenters();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByCostCenters = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setCostCenters((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchCostCenters(token, isNext = true, keywords) {
    const nameOperation = costCentersByCompanyID;
    const nameOperationStr = "costCentersByCompanyID";
    try {
      setCostCentersLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const costCentersData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      code: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const costCentersItems = costCentersData.data[nameOperationStr].items;
      setCostCenters(costCentersItems);
      getNamesByCostCenters(costCentersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: costCentersData.data[nameOperationStr].nextToken,
        };
      });
      setCostCentersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setCostCentersLoading(false);
    } finally {
      setCostCentersLoading(false);
    }
  }
  async function costCenterDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateCostCenter, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateCostCenter.id;
      setCostCenters(costCenters.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        costCenterId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchCostCenters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "30%" },
    },
    {
      key: "code",
      label: strings.table.headers.code,
      _style: { width: "20%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.costCenters.addCostCenter}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {costCentersLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse
          visible={!!costCenterDetails?.id}
          style={styles.collapseCard}
        >
          <CCard key={costCenterDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{costCenterDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.code}:</b> {costCenterDetails?.code}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(costCenterDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, costCenterId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.costCenterName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() =>
              setModalDetails({ visible: false, costCenterId: null })
            }
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => costCenterDelete(modalDetails.costCenterId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalCostCenter.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalCostCenter.visible}
          onClose={() => setModalCostCenter({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalCostCenter.action === "create"
                ? strings.modalCostCenter.addTitle
                : strings.modalCostCenter.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalCostCenter.action === "create" ? (
                <CostCenterCreateForm onSuccess={onSuccessCostCenter} />
              ) : (
                <CostCenterUpdateForm
                  costCenterId={modalCostCenter.costCenterId}
                  onSuccess={onSuccessCostCenter}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Centros de Costo",
    successDeleted: "Centro de Costo eliminado con éxito",
    costCenters: {
      addCostCenter: "Agregar Centro de Costo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar centro de costo",
    },
    table: {
      headers: {
        companyName: "Empresa",
        name: "Nombre",
        code: "Código",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Centro de Costo",
      code: "Código",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el centro de costo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalCostCenter: {
      addTitle: "Agregar Centro de Costo",
      updateTitle: "Actualizar Centro de Costo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default CostCenters;
